import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, Loader2 } from "lucide-react";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { RichView } from "@/components/RichText";

const C = { moss: "#4E6B47", mossDark: "#34482F", mossLight: "#DCE6D2", lichen: "#B9C9A8", stone: "#F1EEE7", stoneDark: "#DDD7CB", slate: "#2E3A40", ink: "#2B3034", muted: "#6A7378" };
const serif = { fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600 };
const sans = { fontFamily: "'Source Sans 3', 'IBM Plex Sans', sans-serif" };

const Mark = ({ className = "w-10 h-5" }) => (
  <svg viewBox="0 0 40 20" className={className} aria-hidden="true">
    <g stroke={C.moss} strokeWidth="3.2" strokeLinecap="round">{[7, 14, 21, 28].map((x) => <line key={x} x1={x} y1={14} x2={x - 1.5} y2={18.5} />)}</g>
    <rect x="2" y="3" width="30" height="12" rx="6" fill={C.moss} />
    <circle cx="33" cy="8" r="5" fill={C.moss} />
    <g stroke="#fff" strokeWidth="0.9">{[10, 17, 24].map((x) => <line key={x} x1={x} y1={5} x2={x} y2={12.5} />)}</g>
    <circle cx="35" cy="6.5" r="0.9" fill="#fff" />
  </svg>
);

const Label = ({ children }) => <div className="uppercase tracking-[0.08em] font-semibold mb-1" style={{ fontSize: "11px", color: C.muted }}>{children}</div>;
const Card = ({ children, className = "", bg = "#fff", testid }) => (
  <div data-testid={testid} className={`rounded-md p-6 sm:p-7 ${className}`} style={{ background: bg, border: `1px solid ${bg === "#fff" ? C.stoneDark : bg}` }}>{children}</div>
);
const Section = ({ title, children, testid }) => (
  <section data-testid={testid} className="mt-12 report-section">
    <h2 className="mb-4" style={{ ...serif, fontSize: "24px", color: C.slate }}>{title}</h2>
    {children}
  </section>
);
const Muted = ({ children }) => <p className="italic" style={{ color: C.muted }}>{children}</p>;

const Header = ({ meta }) => (
  <header className="relative overflow-hidden rounded-t-md px-6 sm:px-10 pt-8 pb-24" style={{ background: C.stone }}>
    <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full" style={{ background: C.mossLight }} />
    <div className="absolute top-36 right-28 w-24 h-24 rounded-full" style={{ background: C.lichen }} />
    <div className="relative flex items-center gap-2.5 mb-10"><Mark /><span className="font-bold tracking-wide" style={{ color: C.slate, fontSize: "14px" }}>TARDIGRADETEAM</span></div>
    <div className="relative uppercase font-bold tracking-[0.1em] mb-2" style={{ fontSize: "11px", color: C.mossDark }}>Participant debrief report</div>
    <h1 data-testid="report-session-name" className="relative leading-tight" style={{ ...serif, fontSize: "clamp(28px, 4vw, 40px)", color: C.slate }}>{meta.session_name}</h1>
  </header>
);

const MetaGrid = ({ meta }) => (
  <Card className="relative -mt-16 mx-4 sm:mx-10" testid="report-meta">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
      {[["Scenario", meta.scenario_title], ["Participant", meta.participant_name], ["Organisation", meta.organisation], ["Date", meta.date], ["Role", meta.role]].map(([k, v]) => (
        <div key={k}><Label>{k}</Label><div className="font-semibold" style={{ color: C.slate }}>{v || "—"}</div></div>
      ))}
      <div data-testid="report-completion"><Label>Completion</Label><div className="font-semibold mb-2" style={{ color: C.slate }}>{meta.completion_pct}%</div>
        <div className="h-1.5 rounded-full" style={{ background: C.stoneDark }}><div className="h-full rounded-full" style={{ width: `${meta.completion_pct}%`, background: C.moss }} /></div>
      </div>
    </div>
  </Card>
);

const InjectCard = ({ inj }) => (
  <Card className="mb-4 report-card" testid={`report-inject-${inj.order}`}>
    <div className="uppercase font-bold tracking-[0.1em]" style={{ fontSize: "11px", color: C.mossDark }}>Inject {inj.order}</div>
    <h3 className="mb-4" style={{ ...serif, fontSize: "19px", color: C.slate }}>{inj.title}</h3>
    {inj.body && <div className="mb-4"><Label>Scenario description</Label><RichView html={inj.body} /></div>}
    {inj.task && <div className="mb-4"><Label>Task</Label><RichView html={inj.task} /></div>}
    <Label>Your response</Label>
    {inj.response
      ? <div className="rounded-sm px-4 py-3" style={{ background: C.stone, borderLeft: `3px solid ${C.moss}` }}><RichView html={inj.response} /></div>
      : <Muted>No response was submitted for this inject.</Muted>}
    {inj.considerations.length > 0 && (
      <div className="mt-5"><Label>Key resilience considerations</Label>
        <ul className="space-y-1">{inj.considerations.map((c) => <li key={c} className="flex gap-2.5"><span style={{ color: C.moss }}>•</span>{c}</li>)}</ul>
      </div>
    )}
  </Card>
);

const Metrics = ({ p }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="report-participation">
    {[["Injects completed", p.injects_completed], ["Injects skipped", p.injects_skipped], ["Responses submitted", p.responses_submitted]].map(([k, v]) => (
      <div key={k} className="rounded-md px-5 py-4" style={{ background: C.stone }}>
        <div style={{ ...serif, fontSize: "32px", color: C.mossDark }}>{v}</div><Label>{k}</Label>
      </div>
    ))}
  </div>
);

const Actions = ({ actions }) => actions.length === 0 ? <Card><Muted>No actions were recorded during this session.</Muted></Card> : (
  <table className="w-full text-left rounded-md overflow-hidden" style={{ border: `1px solid ${C.stoneDark}` }}>
    <thead style={{ background: C.moss, color: "#fff" }}><tr>{["Action", "Owner", "Due date", "Status"].map((h) => <th key={h} className="px-4 py-2 font-semibold text-sm">{h}</th>)}</tr></thead>
    <tbody>{actions.map((a, i) => <tr key={i} style={{ background: i % 2 ? C.stone : "#fff" }}>{["action", "owner", "due_date", "status"].map((k) => <td key={k} className="px-4 py-2">{a[k]}</td>)}</tr>)}</tbody>
  </table>
);

const Feedback = ({ items }) => items.map((f, i) => (
  <Card key={f.question} className="mb-3" testid={`report-feedback-${i}`}>
    <div className="font-semibold mb-1.5" style={{ color: C.slate }}>{f.question}</div>
    {f.answer ? <p className="whitespace-pre-wrap">{f.answer}</p> : <Muted>No feedback was submitted for this question.</Muted>}
  </Card>
));

export default function ParticipantReport() {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const userId = params.get("user");
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/sessions/${sessionId}/participant-report`, { params: userId ? { user_id: userId } : {}, skipAuthRedirect: true })
      .then((r) => setData(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Report unavailable"));
  }, [sessionId, userId]);

  const pdfUrl = `${API}/sessions/${sessionId}/participant-report.pdf?token=${getToken()}${userId ? `&user_id=${userId}` : ""}`;

  return (
    <div className="min-h-screen report-page" style={{ background: "#E7E3DA", color: C.ink, ...sans, fontSize: "16px" }}>
      <div className="no-print sticky top-0 z-10 backdrop-blur-md border-b" style={{ background: "rgba(231,227,218,0.85)", borderColor: C.stoneDark }}>
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <button data-testid="report-back-btn" onClick={() => nav(-1)} className="flex items-center gap-2 text-sm font-semibold hover:opacity-70 transition-opacity" style={{ color: C.slate }}><ArrowLeft className="w-4 h-4" /> Back</button>
          {data && <div className="flex gap-2">
            <button data-testid="report-print-btn" onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm border hover:bg-white/60 transition-colors" style={{ borderColor: C.stoneDark, color: C.slate }}><Printer className="w-4 h-4" /> Print</button>
            <a data-testid="report-download-pdf" href={pdfUrl} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm text-white hover:opacity-90 transition-opacity" style={{ background: C.moss }}><Download className="w-4 h-4" /> Download PDF</a>
          </div>}
        </div>
      </div>
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-8 report-main">
        {error && <Card testid="report-error"><p style={{ color: C.slate }}>{error}</p></Card>}
        {!data && !error && <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin" style={{ color: C.moss }} /></div>}
        {data && (
          <article data-testid="participant-report" className="bg-white rounded-md shadow-sm pb-12 report-paper">
            <Header meta={data.meta} />
            <MetaGrid meta={data.meta} />
            <div className="px-4 sm:px-10">
              <Section title="Executive summary" testid="report-summary">
                <Card bg={C.stone}><p className="leading-relaxed">{data.summary.text}</p>{data.ai_summary && <p className="leading-relaxed mt-3">{data.ai_summary.text}</p>}</Card>
              </Section>
              <Section title="Your responses" testid="report-responses">{data.injects.map((inj) => <InjectCard key={inj.order} inj={inj} />)}</Section>
              <Section title="Participation summary"><Metrics p={data.participation} /></Section>
              <Section title="Personal actions" testid="report-actions"><Actions actions={data.actions} /></Section>
              <Section title="Session feedback" testid="report-feedback"><Feedback items={data.feedback} /></Section>
              <p className="mt-10 text-sm" style={{ color: C.muted }}>{data.note}</p>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
